import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Clock, UserX, Music, UserCheck, AlertCircle } from "lucide-react";
import { Performer } from "@shared/schema";
import { formatTimer } from "@/lib/timer-utils";
import { IsolatedStageTimer } from "@/components/isolated-stage-timer";

interface StageSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  performerName: string;
  availableInstruments: string[];
  onStagePerformers: Performer[];
  onConfirm: (selectedInstruments: string[], replacementPerformerId?: number) => void;
}

export default function StageSelectionModal({
  open,
  onOpenChange,
  performerName,
  availableInstruments,
  onStagePerformers,
  onConfirm,
}: StageSelectionModalProps) {
  const [selectedReplacementId, setSelectedReplacementId] = useState<string>("");
  const [selectedInstruments, setSelectedInstruments] = useState<string[]>([]);
  const [hasManuallyModifiedInstruments, setHasManuallyModifiedInstruments] = useState<boolean>(false);

  // Auto-select instruments when a replacement performer is chosen (only if not manually modified)
  useEffect(() => {
    if (selectedReplacementId && !hasManuallyModifiedInstruments) {
      const replacementPerformer = onStagePerformers.find(
        p => p.id === parseInt(selectedReplacementId)
      );
      
      if (replacementPerformer && replacementPerformer.currentInstruments) {
        // Find instruments that both the new performer and replacement performer can play
        const sharedInstruments = availableInstruments.filter(instrument =>
          replacementPerformer.currentInstruments?.includes(instrument)
        );
        
        // For single selection, only select the first matching instrument
        if (sharedInstruments.length > 0) {
          setSelectedInstruments([sharedInstruments[0]]);
        }
      }
    }
  }, [selectedReplacementId, onStagePerformers, availableInstruments, hasManuallyModifiedInstruments]);

  const handleInstrumentSelect = (instrument: string) => {
    setHasManuallyModifiedInstruments(true);
    setSelectedInstruments([instrument]); // Single selection - replace entire array
  };

  const handleReplacementChange = (value: string) => {
    setSelectedReplacementId(value);
    // Reset manual modification flag when replacement changes
    // This allows auto-selection to work for the new replacement
    setHasManuallyModifiedInstruments(false);
  };

  const handleConfirm = () => {
    const replacementId = selectedReplacementId ? parseInt(selectedReplacementId) : undefined;
    
    // For single instrument performers, auto-select the instrument
    // For multiple instruments, use user selection (which should be exactly one)
    const instruments = availableInstruments.length === 1 ? availableInstruments : selectedInstruments;
    
    // Validation: ensure one instrument is selected for multi-instrument performers
    if (availableInstruments.length > 1 && selectedInstruments.length === 0) {
      return; // Don't proceed if no instrument is selected
    }
    
    onConfirm(instruments, replacementId);
    
    // Reset form
    setSelectedReplacementId("");
    setSelectedInstruments([]);
    setHasManuallyModifiedInstruments(false);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setSelectedReplacementId("");
    setSelectedInstruments([]);
    setHasManuallyModifiedInstruments(false);
    onOpenChange(false);
  };

  const needsInstrumentSelection = availableInstruments.length > 1;
  const canConfirm = !needsInstrumentSelection || selectedInstruments.length === 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-blue-600" />
            Select {performerName} for Stage
          </DialogTitle>
          <DialogDescription>
            {onStagePerformers.length > 0 
              ? "Choose replacement options and instruments for this performer."
              : "Select instruments for this performer to go on stage."
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Replacement Selection */}
          {onStagePerformers.length > 0 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Replace performer (optional)
                </label>
                <Select value={selectedReplacementId} onValueChange={handleReplacementChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select performer to replace (or leave blank)" />
                  </SelectTrigger>
                  <SelectContent>
                    {onStagePerformers.map((performer) => (
                      <SelectItem key={performer.id} value={performer.id.toString()}>
                        <div className="flex items-center justify-between w-full">
                          <span className="font-medium">{performer.name}</span>
                          <div className="flex items-center ml-4 space-x-2">
                            <Badge variant="outline" className="text-xs">
                              {performer.currentInstruments?.join(", ") || "No instruments"}
                            </Badge>
                            <div className="flex items-center text-orange-600">
                              <Clock className="h-3 w-3 mr-1" />
                              <IsolatedStageTimer 
                                stageStartTime={performer.stageStartTime}
                                className="font-mono text-xs"
                              />
                            </div>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              
            </div>
          )}

          {/* Instrument Selection */}
          {needsInstrumentSelection && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block flex items-center gap-2">
                  <Music className="h-4 w-4" />
                  Select Instrument for {performerName} <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-slate-600 mb-3">
                  Choose which instrument {performerName} will be playing:
                </p>
                
                <div className="space-y-3">
                  {availableInstruments.map((instrument) => (
                    <div key={instrument} className="flex items-center space-x-3">
                      <input
                        type="radio"
                        id={`stage-instrument-${instrument}`}
                        name="stage-instrument"
                        value={instrument}
                        checked={selectedInstruments.includes(instrument)}
                        onChange={() => handleInstrumentSelect(instrument)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label 
                        htmlFor={`stage-instrument-${instrument}`}
                        className="text-sm capitalize cursor-pointer flex-1"
                      >
                        {instrument}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {selectedInstruments.length === 0 && (
                <div className="flex items-center space-x-2 text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">Please select an instrument to continue</span>
                </div>
              )}
            </div>
          )}



          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed"
              style={{
                boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              {selectedReplacementId ? "Replace & Select for Stage" : "Select for Stage"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}